import {
  ActionButton,
  Paginator,
  Show,
  TableComponent,
  YearHeader,
} from "components";
import { FC, useMemo } from "react";
import {
  Payment_status,
  status_type,
} from "_graphql/__generated__/globalTypes-kek";
import {
  extractVehicleNumberFromDetails,
  useUrlState,
  withPermissions,
} from "utils";
import { useAppNavigate, useAppSearch } from "_graphql/cache/auth";

import { Action } from "components/buttons/action-button";
import AttachSupportingDocumentsContainer from "pages/create-slip/attach-supporting-documents";
import FilterMenu from "./filter";
import RemoveContainer from "./remove";
import RenewContainer from "./renew";
import ReopenOfferContainer from "./reopen";
import SendEmailsContainer from "./send-documents";
import SendMessageContainer from "pages/place-it-offers/message-thread";
import ViewNotesContainer from "./view";
import _ from "lodash";
import clsx from "clsx";
import moment from "moment";
import numeral from "numeral";
import { useKEKOffers } from "hooks/data/use-offers";
import { usePagination } from "hooks";
import { useUpdateBubble } from "hooks/data/use-update-bubble";

type Props = {};

const CreateClosingPage: FC<Props> = () => {
  const [modal, setModal] = useUrlState("modal");
  const searchParams = useAppSearch();
  const navigate = useAppNavigate();
  const { offset, limit, setPage, search } = usePagination();
  const { updateBubble } = useUpdateBubble();

  const filter = useMemo(
    () => ({
      page: searchParams.page || 1,
      pageSize: searchParams.pageSize || 10,
      search: (search || "").toString(),
      args: {
        status: ["CLOSED"],
        insurer_id: searchParams.insurer ? searchParams.insurer : undefined,
        year: searchParams.year?.toString() || "",
        offer_from: searchParams.offer_from || "",
        class_of_business_id: searchParams.business
          ? searchParams.business
          : undefined,
        payment_status: searchParams.payment_status
          ? [searchParams.payment_status]
          : [],
        claim_status: [],
        approval_status: searchParams.approval_status || undefined,
        expiry_status: searchParams.expiry_status || undefined,
        staff_id: searchParams.employee
          ? [Number(searchParams.employee)]
          : undefined,
        search: search || "",
      },
    }),
    [
      searchParams.insurer,
      searchParams.business,
      searchParams.payment_status,
      searchParams.approval_status,
      searchParams.page,
      searchParams.pageSize,
      searchParams.year,
      search,
    ]
  );

  const dispatchAction =
    (id: string, action: Exclude<Action, "expand" | "goto" | "clone">) =>
    () => {
      navigate({
        search: (old) => ({
          ...old,
          id,
          modal: action,
        }),
      });
    };

  const expandItem = (id: string) => {
    navigate({
      to: `./${id}/credit-notes`,
    });
  };

  const updateThreadBubble = (ids: string[]) => {
    updateBubble({
      variables: {
        ids,
      },
    })
      .then(() => {})
      .catch(() => {});
  };

  const { offers, loading, refetch, count } = useKEKOffers(filter as any);

  return (
    <main className="flex-1 flex flex-col overflow-hidden bg-background-500">
      <YearHeader />
      {/* {JSON.stringify(form.values.insurer, null, 2)} */}
      <div className="flex flex-1 overflow-y-auto">
        <div className="flex-1 mx-auto min-w-0  py-4 sm:py-6 overflow-hidden overflow-y-auto light flex">
          <TableComponent
            title={"offers"}
            hasSearch
            loading={loading}
            columns={[
              {
                name: "Policy #",
                accessor: "offer_detail.policy_number",
                render: (value) => (
                  <div className="flex flex-col items-start flex-wrap break-all break-words">
                    <div className="flex flex-row items-center gap-x-1">
                      <span className="font-bold text-xs overflow-hidden text-ellipsis  ">
                        {value.offer_detail?.policy_number || "N/A"}{" "}
                        <Show if={Boolean(value.offer_retrocedent?.created_at)}>
                          <span>
                            -{" "}
                            <span className="text-xs text-red-500 italic">
                              Retro
                            </span>
                          </span>
                        </Show>
                      </span>
                    </div>
                    <div
                      className={clsx(
                        "p-0.5 rounded-full",
                        {
                          " text-green-600": value.offer_from === "INTERNAL",
                          " text-yellow-600": value.offer_from !== "INTERNAL",
                        },
                        "text-xs font-medium"
                      )}
                    >
                      <span>{value.offer_from}</span>
                    </div>
                  </div>
                ),
              },
              {
                name: "Reinsured",
                accessor: "insurer.insurer_company_name",
                render: (value) => (
                  <div className="flex flex-col text-xs">
                    <span className="font-medium">
                      {value.insurer?.insurer_company_name || "N/A"}
                    </span>
                    <span className="font-light">
                      {value.insurer?.insurer_company_email || "N/A"}
                    </span>
                  </div>
                ),
              },
              {
                name: "Insured",
                accessor: "offer_detail.insured_by",
                render: (value) => {
                  const details = JSON.parse(
                    value?.offer_detail?.offer_details || "[]"
                  );
                  return (
                    <div className="flex flex-col text-xs ">
                      <span className="font-medium">
                        {value.offer_detail?.insured_by || "N/A"}
                      </span>
                      <span className="font-light">
                        {[
                          "Motor Comprehensive Fleet",
                          "Motor Comprehensive",
                          "Motor Comprehensive (Automobile Fac Facility)",
                        ].includes(value?.classofbusiness?.business_name || "")
                          ? extractVehicleNumberFromDetails(details)
                          : ""}
                      </span>
                    </div>
                  );
                },
              },
              {
                name: "Class Of Business",
                accessor: "classofbusiness.business_name",
                render: (value) => (
                  <div className="flex flex-col text-xs">
                    <span className="font-medium">
                      {value.classofbusiness?.business_name || "N/A"}
                    </span>
                  </div>
                ),
              },
              {
                name: "Sum Insured",
                accessor: "sum_insured",
                render: (value) => (
                  <div className="flex flex-col text-xs">
                    <span className="font-medium">
                      {value?.offer_detail?.currency}{" "}
                      {numeral(value.sum_insured).format("0,0 ") || "N/A"}
                    </span>
                  </div>
                ),
              },
              {
                name: "Premium",
                accessor: "premium",
                render: (value) => (
                  <div className="flex flex-col text-xs">
                    <span className="font-medium">
                      {value?.offer_detail?.currency}{" "}
                      {numeral(value.premium).format("0,0 ") || "N/A"}
                    </span>
                  </div>
                ),
              },
              {
                name: "Offer Date",
                accessor: "created_at",
                render: (value) => (
                  <span>
                    {moment(value.created_at).format("ddd MMM D, YYYY")}
                  </span>
                ),
              },
              {
                name: "Statuses",
                accessor: "expiry_status",
                render: (value) => (
                  <div className="flex flex-col items-start">
                    <div
                      className={clsx(
                        "p-1 rounded-lg mb-1",
                        {
                          "bg-green-600/30 text-green-600":
                            value.expiry_status === "ACTIVE",
                          "bg-red-300/30 text-red-600":
                            value.expiry_status === "EXPIRED",
                        },
                        "text-xs font-medium"
                      )}
                    >
                      <span>{value.expiry_status}</span>
                    </div>
                    <div
                      className={clsx(
                        "p-1 rounded-lg mb-1",
                        {
                          "bg-green-600/30 text-green-600":
                            value.approval_status === "APPROVED",
                          "bg-yellow-300/30 text-yellow-600":
                            value.approval_status === "UNAPPROVED",
                        },
                        "text-xs font-medium"
                      )}
                    >
                      <span>{value.approval_status}</span>
                    </div>
                    <div
                      className={clsx(
                        "p-1 rounded-lg",
                        {
                          "bg-green-600/30 text-green-600":
                            value.payment_status === "PAID",
                          "bg-red-300/30 text-red-600":
                            value.payment_status === "UNPAID",
                          "bg-sky-600/30 text-sky-600":
                            value.payment_status === "PARTPAYMENT",
                        },
                        "text-xs font-medium"
                      )}
                    >
                      <span>{value.payment_status}</span>
                    </div>
                  </div>
                ),
              },
              // {
              //   name: "Payment Status",
              //   accessor: "payment_status",
              //   render: (value) => (
              //     <div className="flex">
              //       <div
              //         className={clsx(
              //           "p-1 rounded-lg",
              //           {
              //             "bg-green-600/30 text-green-600":
              //               value.payment_status === "PAID",
              //             "bg-red-300/30 text-red-600":
              //               value.payment_status === "UNPAID",
              //             "bg-sky-600/30 text-sky-600":
              //               value.payment_status === "PARTPAYMENT",
              //           },
              //           "text-xs font-medium"
              //         )}
              //       >
              //         <span>{value.payment_status}</span>
              //       </div>
              //     </div>
              //   ),
              // },
              // {
              //   name: "Approval Status",
              //   accessor: "approval_status",
              //   render: (value) => (
              //     <div className="flex">
              //       <div
              //         className={clsx(
              //           "p-1 rounded-lg",
              //           {
              //             "bg-green-600/30 text-green-600":
              //               value.approval_status === "APPROVED",
              //             "bg-yellow-300/30 text-yellow-600":
              //               value.approval_status === "UNAPPROVED",
              //           },
              //           "text-xs font-medium"
              //         )}
              //       >
              //         <span>{value.approval_status}</span>
              //       </div>
              //     </div>
              //   ),
              // },
              {
                name: "Created By",
                accessor: "employee.employee_first_name",
                render: (value) => (
                  <div className="flex flex-row leading-0">
                    <span>
                      {value?.employee?.employee_first_name}{" "}
                      {value?.employee?.employee_last_name}
                    </span>
                  </div>
                ),
              },
              {
                name: "Business State",
                accessor: "business_state",
              },
              {
                name: "Payment Type",
                accessor: "payment_type",
                render(data, index) {
                  return (
                    <span className="text-xs">
                      {_.startCase(
                        Object.keys(
                          JSON.parse(data.offer_detail?.payment_type || "{}")
                        ).at(0) || "N/A"
                      )}
                    </span>
                  );
                },
              },
              {
                name: "Total Securities",
                accessor: "total_securities",
                render(data) {
                  return (
                    <span className="text-xs">
                      {data?.offer_participant?.length || 0}
                    </span>
                  );
                },
              },
              {
                name: "Actions",
                accessor: "actions",
                render: (value) => (
                  <>
                    <ActionButton
                      action="view"
                      tooltip="View Documents & History"
                      onClick={dispatchAction(
                        value.offer_id +
                          ":" +
                          value?.offer_from +
                          ":" +
                          value?.place_it_id,
                        "view"
                      )}
                    />
                    {value.offer_from !== "INTERNAL" && (
                      <ActionButton
                        action="chat"
                        tooltip="View Message Thread"
                        onClick={dispatchAction(
                          value?.place_it_id || "",
                          "chat"
                        )}
                        count={
                          value.thread_notification?.filter(
                            (item) => item?.status !== "READ"
                          ).length
                        }
                        callBack={() =>
                          updateThreadBubble(
                            value.thread_notification?.map(
                              (item) => item?.id || ""
                            ) || []
                          )
                        }
                      />
                    )}
                    {withPermissions([
                      "*:*",
                      "offers:*",
                      "offers:manage",
                      "offers:send",
                    ])(
                      <ActionButton
                        action="send"
                        disabled={
                          value?.approval_status === status_type.UNAPPROVED
                        }
                        tooltip="Send Notes"
                        onClick={dispatchAction(value?.offer_id, "send")}
                      />
                    )}
                    <ActionButton
                      action="goto"
                      tooltip="View Credit Notes"
                      onClick={() => expandItem(value?.offer_id)}
                    />
                    {withPermissions([
                      "*:*",
                      "offers:*",
                      "offers:manage",
                      "offers:reopen",
                    ])(
                      <ActionButton
                        action="reject"
                        tooltip="Renew Offer"
                        onClick={dispatchAction(value?.offer_id, "reject")}
                      />
                    )}
                    <ActionButton
                      action="lock-open"
                      tooltip="Reopen Offer"
                      onClick={dispatchAction(value?.offer_id, "recreate")}
                    />
                    <Show if={value.offer_from === "INTERNAL"}>
                      {withPermissions([
                        "*:*",
                        "offers:*",
                        "offers:manage",
                        "offers:attach_docs",
                      ])(
                        <ActionButton
                          action="attach"
                          tooltip="Add Supporting Documents"
                          onClick={dispatchAction(value?.offer_id, "import")}
                        />
                      )}
                    </Show>
                    {withPermissions([
                      "*:*",
                      "offers:*",
                      "offers:manage",
                      "offers:delete",
                    ])(
                      <ActionButton
                        action="remove"
                        disabled={
                          value?.payment_status === Payment_status.PAID ||
                          value?.payment_status === Payment_status.PARTPAYMENT
                        }
                        tooltip={
                          value?.payment_status === Payment_status.PAID ||
                          value?.payment_status === Payment_status.PARTPAYMENT
                            ? "Cannot Cancel Paid Offer"
                            : "Cancel Offer"
                        }
                        onClick={dispatchAction(value?.offer_id, "remove")}
                      />
                    )}
                  </>
                ),
              },
            ]}
            data={offers || []}
            refetch={refetch}
            renderFilter={({ filterOpen, setFilterOpen }) => (
              <FilterMenu open={filterOpen} setOpen={setFilterOpen} />
            )}
            pagination={() => (
              <Paginator
                offset={offset}
                limit={limit}
                totalItems={count || 0}
                currentSize={offers.length}
                setOffset={setPage}
              />
            )}
          />
        </div>
      </div>
      {searchParams?.id && modal === "view" && (
        <ViewNotesContainer
          open={modal === "view"}
          setOpen={(val: boolean) => setModal(val ? "view" : undefined)}
        />
      )}
      {searchParams?.id && modal === "recreate" && (
        <ReopenOfferContainer
          open={modal === "recreate"}
          setOpen={(val: boolean) => setModal(val ? "recreate" : undefined)}
        />
      )}
      {searchParams?.id && modal === "remove" && (
        <RemoveContainer
          open={modal === "remove"}
          setOpen={(val: boolean) => setModal(val ? "remove" : undefined)}
        />
      )}
      {searchParams?.id && modal === "reject" && (
        <RenewContainer
          open={modal === "reject"}
          setOpen={(val: boolean) => setModal(val ? "reject" : undefined)}
        />
      )}
      {searchParams?.id && modal === "send" && (
        <SendEmailsContainer
          open={modal === "send"}
          setOpen={(val: boolean) => setModal(val ? "send" : undefined)}
        />
      )}
      {searchParams?.id && modal === "chat" && (
        <SendMessageContainer
          open={modal === "chat"}
          setOpen={(val: boolean) => setModal(val ? "chat" : undefined)}
        />
      )}
      {searchParams?.id && modal === "import" && (
        <AttachSupportingDocumentsContainer
          open={modal === "import"}
          setOpen={(val: boolean) => setModal(val ? "import" : undefined)}
        />
      )}
    </main>
  );
};

export default CreateClosingPage;
